'use client';

import Link from 'next/link';
import { ChevronDown } from 'lucide-react';

import { cn } from '@naumu/ui/utils/cn';

import type { MarketingNavLink } from '../config/marketing-nav';

const TRIGGER_CLASS =
	'flex items-center gap-1 rounded-lg px-3 py-2 text-[14px] font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white';

interface MarketingNavItemProps {
	link: MarketingNavLink;
	/** True when this item's popover is the one currently shown (rotates the chevron). */
	isOpen: boolean;
	/** Fired on hover/focus; passes the trigger element so the parent can position the popover. */
	onTriggerEnter: (trigger: HTMLElement) => void;
	/** Fired on leave/blur; the parent schedules the popover to close. */
	onTriggerLeave: () => void;
}

/**
 * MarketingNavItem - a single header nav trigger (plain link, or a link/button
 * with a chevron when the nav config gives it `items`). Purely presentational:
 * it reports hover/focus to MarketingNavLinks, which owns the single shared
 * popover that travels between triggers.
 */
export const MarketingNavItem = ({
	link,
	isOpen,
	onTriggerEnter,
	onTriggerLeave,
}: MarketingNavItemProps) => {
	const hasItems = !!link.items?.length;

	const handlers = {
		onMouseEnter: (e: React.MouseEvent<HTMLElement>) => onTriggerEnter(e.currentTarget),
		onMouseLeave: onTriggerLeave,
		onFocus: (e: React.FocusEvent<HTMLElement>) => onTriggerEnter(e.currentTarget),
		onBlur: onTriggerLeave,
	};

	// Plain link - no sub-items. Still reports enter so it closes any open popover.
	if (!hasItems) {
		return (
			<Link href={link.href ?? '#'} className={TRIGGER_CLASS} {...handlers}>
				{link.label}
			</Link>
		);
	}

	const chevron = (
		<ChevronDown
			className={cn('size-3.5 text-white/40 transition-transform duration-200', isOpen && 'rotate-180')}
			strokeWidth={2}
		/>
	);

	// Keep the hover background latched while the popover is open, so it persists
	// even when the pointer moves off the trigger and into the panel.
	const triggerClass = cn(TRIGGER_CLASS, isOpen && 'bg-white/5 text-white');

	return link.href ? (
		<Link
			href={link.href}
			className={triggerClass}
			aria-haspopup="menu"
			aria-expanded={isOpen}
			{...handlers}
		>
			{link.label}
			{chevron}
		</Link>
	) : (
		<button type="button" className={triggerClass} aria-haspopup="menu" aria-expanded={isOpen} {...handlers}>
			{link.label}
			{chevron}
		</button>
	);
};
